import * as THREE from 'three'
import GUI from 'lil-gui'

const canvas = document.querySelector('canvas.webgl')
const scene = new THREE.Scene()
scene.background = new THREE.Color('#63073A')

const COUNT = 12000
const LUT_N = 200
const CYCLE = 14.0

// Ellipses open VERTICALLY — tall, top/bottom go off-screen
const OUTER_RX = 5.5, OUTER_RY = 9.0
const INNER_RX = 3.0, INNER_RY = 4.8

const OUTER_N = 4200
const INNER_N = 4200
const BG_N = COUNT - OUTER_N - INNER_N // 3600 background particles

function noise(x, y, z) {
    return Math.sin(x * 1.27 + y * 3.17 + z * 2.41) * 0.35
         + Math.sin(x * 2.91 - y * 1.73 + z * 0.89) * 0.25
         + Math.sin(-x * 0.67 + y * 2.53 + z * 3.11) * 0.20
         + Math.sin(x * 3.71 + y * 0.59 - z * 2.17) * 0.12
         + Math.sin(x * 0.37 - y * 3.91 - z * 1.63) * 0.08
}

function buildLUT(pts) {
    const curve = new THREE.CatmullRomCurve3(
        pts.map(p => new THREE.Vector3(p[0], p[1], 0)),
        false, 'catmullrom', 0.5
    )
    const pos = new Float32Array((LUT_N + 1) * 2)
    const nrm = new Float32Array((LUT_N + 1) * 2)
    for (let i = 0; i <= LUT_N; i++) {
        const t = i / LUT_N
        const p = curve.getPoint(t)
        const tn = curve.getTangent(t)
        const len = Math.hypot(tn.x, tn.y) || 1
        pos[i * 2] = p.x
        pos[i * 2 + 1] = p.y
        nrm[i * 2] = -tn.y / len
        nrm[i * 2 + 1] = tn.x / len
    }
    return { pos, nrm }
}

function sampleLUT(lut, t) {
    t = ((t % 1) + 1) % 1
    const ft = t * LUT_N
    const i = Math.min(Math.floor(ft), LUT_N - 1)
    const f = ft - i
    const a = i * 2, b = a + 2
    return {
        x: lut.pos[a] + (lut.pos[b] - lut.pos[a]) * f,
        y: lut.pos[a + 1] + (lut.pos[b + 1] - lut.pos[a + 1]) * f,
        nx: lut.nrm[a] + (lut.nrm[b] - lut.nrm[a]) * f,
        ny: lut.nrm[a + 1] + (lut.nrm[b + 1] - lut.nrm[a + 1]) * f,
    }
}

const streams = [
    { pts: [[-7, -5], [-6, -3], [-5.5, -1], [-5.5, 1], [-6, 3], [-7, 5]], w: 0.35, spd: 0.03 },
    { pts: [[-6, -1.5], [-4.8, 0], [-3.5, 1], [-2, 2], [-0.5, 3], [0.5, 4.5]], w: 0.28, spd: 0.035 },
    { pts: [[-7, 3], [-5, 3.5], [-3, 3.5], [-1, 3.8], [1, 4.5]], w: 0.25, spd: 0.03 },
    { pts: [[7, -5], [5, -2.5], [3.8, -0.5], [3.8, 1], [4.5, 2.5], [5.5, 4], [7, 5]], w: 0.35, spd: 0.025 },
    { pts: [[-5.5, -5], [-2, -3], [0, -2.5], [2, -3], [5.5, -5]], w: 0.22, spd: 0.028 },
]

const luts = streams.map(s => buildLUT(s.pts))

// 0 = outer circle, 1 = inner circle, 2 = background (always scattered)
const pGroup = new Uint8Array(COUNT)
const cAngle = new Float32Array(COUNT)
const cPerp = new Float32Array(COUNT)
const cRotSpd = new Float32Array(COUNT)

const sCurve = new Int32Array(COUNT)
const sBaseT = new Float32Array(COUNT)
const sPerp = new Float32Array(COUNT)
const sFlowSpd = new Float32Array(COUNT)
const sAtmX = new Float32Array(COUNT)
const sAtmY = new Float32Array(COUNT)

const bgSeedA = new Float32Array(COUNT)
const bgSeedB = new Float32Array(COUNT)

const fBias = new Float32Array(COUNT)
const pSz = new Float32Array(COUNT)
const pZ = new Float32Array(COUNT)
const introStartX = new Float32Array(COUNT)
const introStartY = new Float32Array(COUNT)

const sCumProb = [0.18, 0.30, 0.38, 0.56, 0.68]

for (let i = 0; i < COUNT; i++) {
    pZ[i] = (Math.random() - 0.5) * 0.1
    bgSeedA[i] = Math.random() * 100
    bgSeedB[i] = Math.random() * 100
    introStartX[i] = (Math.random() - 0.5) * 16
    introStartY[i] = (Math.random() - 0.5) * 12

    if (i < OUTER_N) {
        // Outer circle
        pGroup[i] = 0
        cAngle[i] = 0.6 * Math.PI + (i / OUTER_N) * 1.8 * Math.PI + (Math.random() - 0.5) * 0.08
        const g = (Math.random() + Math.random() + Math.random() - 1.5) / 1.5
        cPerp[i] = g * 0.3
        cRotSpd[i] = 0.008 + Math.random() * 0.004
        fBias[i] = Math.random() * 0.3
        pSz[i] = (0.06 + (1 - Math.abs(g)) * 0.12) * (0.7 + Math.random() * 0.6)
    } else if (i < OUTER_N + INNER_N) {
        // Inner circle
        pGroup[i] = 1
        cAngle[i] = 0.6 * Math.PI + ((i - OUTER_N) / INNER_N) * 1.8 * Math.PI + (Math.random() - 0.5) * 0.08
        const g = (Math.random() + Math.random() + Math.random() - 1.5) / 1.5
        cPerp[i] = g * 0.25
        cRotSpd[i] = -0.006 - Math.random() * 0.003
        fBias[i] = 0.35 + Math.random() * 0.65
        pSz[i] = (0.05 + (1 - Math.abs(g)) * 0.1) * (0.7 + Math.random() * 0.6)
    } else {
        // Background — always scattered, no circle participation
        pGroup[i] = 2
        fBias[i] = 0
        pSz[i] = 0.03 + Math.random() * 0.07
    }

    // Stream / atmospheric assignment (for the "streams" state)
    const r = Math.random()
    let assigned = false
    for (let s = 0; s < sCumProb.length; s++) {
        if (r < sCumProb[s]) {
            sCurve[i] = s
            sBaseT[i] = Math.random()
            const g2 = (Math.random() + Math.random() + Math.random() - 1.5) / 1.5
            sPerp[i] = g2 * streams[s].w
            sFlowSpd[i] = streams[s].spd * (0.8 + Math.random() * 0.4)
            assigned = true
            break
        }
    }
    if (!assigned) {
        sCurve[i] = -1
        const sc = Math.floor(Math.random() * streams.length)
        const st = 0.1 + Math.random() * 0.8
        const sam = sampleLUT(luts[sc], st)
        const pd = (Math.random() - 0.5) * 3.5
        sAtmX[i] = sam.x + sam.nx * pd + (Math.random() - 0.5) * 0.5
        sAtmY[i] = sam.y + sam.ny * pd + (Math.random() - 0.5) * 0.5
    }
}

const geometry = new THREE.BufferGeometry()
const positions = new Float32Array(COUNT * 3)
const colors = new Float32Array(COUNT * 3)
const sizes = new Float32Array(COUNT)

const baseColor = new THREE.Color('#FF3573')
for (let i = 0; i < COUNT; i++) {
    const v = 0.88 + Math.random() * 0.24
    colors[i * 3] = Math.min(baseColor.r * v, 1)
    colors[i * 3 + 1] = Math.min(baseColor.g * v, 1)
    colors[i * 3 + 2] = Math.min(baseColor.b * v, 1)
    sizes[i] = pSz[i]
}

geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3))
geometry.setAttribute('aSize', new THREE.BufferAttribute(sizes, 1))

const material = new THREE.ShaderMaterial({
    vertexShader: `
        attribute float aSize;
        varying vec3 vColor;
        void main() {
            vColor = color;
            vec4 mv = modelViewMatrix * vec4(position, 1.0);
            gl_PointSize = aSize * (200.0 / -mv.z);
            gl_PointSize = max(gl_PointSize, 1.0);
            gl_Position = projectionMatrix * mv;
        }
    `,
    fragmentShader: `
        varying vec3 vColor;
        void main() {
            float d = length(gl_PointCoord - 0.5);
            if (d > 0.5) discard;
            float edge = 1.0 - smoothstep(0.3, 0.5, d);
            gl_FragColor = vec4(vColor, edge * 0.85);
        }
    `,
    transparent: true,
    depthWrite: false,
    blending: THREE.NormalBlending,
    vertexColors: true,
})

const points = new THREE.Points(geometry, material)
scene.add(points)

const dim = { w: window.innerWidth, h: window.innerHeight }
const camera = new THREE.PerspectiveCamera(55, dim.w / dim.h, 0.1, 100)
camera.position.z = 7
scene.add(camera)

const renderer = new THREE.WebGLRenderer({ canvas, antialias: true })
renderer.setSize(dim.w, dim.h)
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))

window.addEventListener('resize', () => {
    dim.w = window.innerWidth
    dim.h = window.innerHeight
    camera.aspect = dim.w / dim.h
    camera.updateProjectionMatrix()
    renderer.setSize(dim.w, dim.h)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
})

// Mouse interaction — magnetic repulsion
const mouse = { x: 0, y: 0, active: false }

const params = {
    mouseRadius: 4.5,
    mouseStrength: 0.3,
    cycle: 20,
    outerRx: 5.5,
    outerRy: 10.5,
    innerRx: 2.8,
    innerRy: 4.2,
    bgColor: '#63073A',
    particleColor: '#FF3573',
    outerRotSpeed: 0.008,
    innerRotSpeed: -0.006,
    streamWidth: 0.5,
    bgParticleSpread: 2.5,
    innerSpread: 3.2,
    outerSpread: 2.3,
}

if (window.location.hash === '#debug') {
    const gui = new GUI({ title: 'Particle Debug' })
    const fMouse = gui.addFolder('Mouse Interaction')
    fMouse.add(params, 'mouseRadius', 0.2, 8, 0.1).name('Raio')
    fMouse.add(params, 'mouseStrength', 0.05, 3, 0.05).name('Força')

    const fTiming = gui.addFolder('Timing')
    fTiming.add(params, 'cycle', 4, 30, 1).name('Ciclo (s)')

    const fEllipse = gui.addFolder('Elipses')
    fEllipse.add(params, 'outerRx', 2, 12, 0.5).name('Ext. Largura')
    fEllipse.add(params, 'outerRy', 2, 14, 0.5).name('Ext. Altura')
    fEllipse.add(params, 'innerRx', 1, 8, 0.5).name('Int. Largura')
    fEllipse.add(params, 'innerRy', 1, 10, 0.5).name('Int. Altura')
    fEllipse.add(params, 'outerRotSpeed', -0.15, 0.15, 0.005).name('Ext. Rotação')
    fEllipse.add(params, 'innerRotSpeed', -0.15, 0.15, 0.005).name('Int. Rotação')
    fEllipse.add(params, 'innerSpread', 0.3, 5, 0.1).name('Int. Spread')
    fEllipse.add(params, 'outerSpread', 0.3, 5, 0.1).name('Ext. Spread')

    const fVisual = gui.addFolder('Visual')
    fVisual.addColor(params, 'bgColor').name('Fundo').onChange(v => scene.background.set(v))
    fVisual.addColor(params, 'particleColor').name('Partículas').onChange(v => {
        const c = new THREE.Color(v)
        for (let i = 0; i < COUNT; i++) {
            colors[i * 3] = Math.min(c.r * (0.88 + Math.random() * 0.24), 1)
            colors[i * 3 + 1] = Math.min(c.g * (0.88 + Math.random() * 0.24), 1)
            colors[i * 3 + 2] = Math.min(c.b * (0.88 + Math.random() * 0.24), 1)
        }
        geometry.attributes.color.needsUpdate = true
    })
    fVisual.add(params, 'streamWidth', 0.3, 3, 0.1).name('Largura Streams')
    fVisual.add(params, 'bgParticleSpread', 0.3, 2, 0.1).name('Spread BG')
}

function updateMouse(clientX, clientY) {
    const ndcX = (clientX / dim.w) * 2 - 1
    const ndcY = -(clientY / dim.h) * 2 + 1
    const halfH = Math.tan(camera.fov * Math.PI / 360) * camera.position.z
    const halfW = halfH * camera.aspect
    mouse.x = ndcX * halfW
    mouse.y = ndcY * halfH
    mouse.active = true
}

window.addEventListener('mousemove', (e) => updateMouse(e.clientX, e.clientY))
window.addEventListener('mouseleave', () => { mouse.active = false })

window.addEventListener('message', (e) => {
    if (e.data && e.data.type === 'mousemove') {
        updateMouse(e.data.x * dim.w, e.data.y * dim.h)
    } else if (e.data && e.data.type === 'mouseleave') {
        mouse.active = false
    }
})

const clock = new THREE.Clock()

function tick() {
    const t = clock.getElapsedTime()
    const pos = geometry.attributes.position.array

    const INTRO_DUR = 3.0
    const introP = Math.min(t / INTRO_DUR, 1)
    const introEase = introP < 1 ? introP * introP * (3 - 2 * introP) : 1

    const cycleTime = Math.max(0, t - INTRO_DUR)
    const curCycle = params.cycle
    const cycle = (cycleTime % curCycle) / curCycle
    let globalF
    if (cycle < 0.20) globalF = 0
    else if (cycle < 0.35) globalF = 0.5 - 0.5 * Math.cos(((cycle - 0.20) / 0.15) * Math.PI)
    else if (cycle < 0.70) globalF = 1
    else globalF = 0.5 + 0.5 * Math.cos(((cycle - 0.70) / 0.30) * Math.PI)

    for (let i = 0; i < COUNT; i++) {
        let finalX, finalY

        if (pGroup[i] === 2) {
            finalX = noise(bgSeedA[i], t * 0.04, bgSeedB[i]) * 7 * params.bgParticleSpread
            finalY = noise(bgSeedB[i], bgSeedA[i], t * 0.04) * 4.5 * params.bgParticleSpread
            if (mouse.active) {
                const dx = finalX - mouse.x
                const dy = finalY - mouse.y
                const dist = Math.sqrt(dx * dx + dy * dy)
                if (dist < params.mouseRadius && dist > 0.01) {
                    const t2 = 1 - dist / params.mouseRadius
                    const push = t2 * t2 * (3 - 2 * t2) * params.mouseStrength
                    finalX += (dx / dist) * push
                    finalY += (dy / dist) * push
                }
            }
        } else {
            const rawF = (globalF - fBias[i] * 0.4) / (1 - fBias[i] * 0.4)
            const maxF = pGroup[i] === 0 ? 1.0 : 0.7
            const f = Math.max(0, Math.min(maxF, rawF))

            const baseRot = pGroup[i] === 0 ? params.outerRotSpeed : params.innerRotSpeed
            const angle = cAngle[i] + t * (cRotSpd[i] + baseRot) * 0.5
            const rx = pGroup[i] === 0 ? params.outerRx : params.innerRx
            const ry = pGroup[i] === 0 ? params.outerRy : params.innerRy
            const sinA = Math.sin(angle)
            const bellyMod = 1.0 + 0.3 * (1 - sinA * sinA) - 0.2 * sinA
            let cx = rx * bellyMod * Math.cos(angle)
            let cy = ry * sinA
            const tx = -rx * Math.sin(angle)
            const ty = ry * Math.cos(angle)
            const tLen = Math.hypot(tx, ty) || 1
            const spread = pGroup[i] === 0 ? params.outerSpread : params.innerSpread
            cx += (-ty / tLen) * cPerp[i] * spread
            cy += (tx / tLen) * cPerp[i] * spread
            cx += noise(cx * 0.3, cy * 0.3, t * 0.15) * 0.1 * spread
            cy += noise(cy * 0.3, t * 0.15, cx * 0.3) * 0.1 * spread

            let sx, sy
            if (sCurve[i] >= 0) {
                const ct = (sBaseT[i] + t * sFlowSpd[i]) % 1
                const s = sampleLUT(luts[sCurve[i]], ct)
                const n = noise(s.x * 0.5 + t * 0.1, s.y * 0.5, pZ[i] * 5)
                const perpOff = (sPerp[i] + n * streams[sCurve[i]].w * 0.3) * params.streamWidth
                const ux = noise(s.x * 0.3, s.y * 0.3, t * 0.15) * 0.12
                const uy = noise(s.y * 0.3, t * 0.15, s.x * 0.3) * 0.12
                sx = s.x + s.nx * perpOff + ux
                sy = s.y + s.ny * perpOff + uy
            } else {
                sx = sAtmX[i] + noise(sAtmX[i] * 0.3 + t * 0.05, sAtmY[i] * 0.3, pZ[i]) * 0.4
                sy = sAtmY[i] + noise(pZ[i], sAtmX[i] * 0.3 - t * 0.04, sAtmY[i] * 0.3) * 0.4
            }

            finalX = cx + (sx - cx) * f
            finalY = cy + (sy - cy) * f

            if (mouse.active) {
                const dx = finalX - mouse.x
                const dy = finalY - mouse.y
                const dist = Math.sqrt(dx * dx + dy * dy)
                if (dist < params.mouseRadius && dist > 0.01) {
                    const t2 = 1 - dist / params.mouseRadius
                    const push = t2 * t2 * (3 - 2 * t2) * params.mouseStrength
                    finalX += (dx / dist) * push
                    finalY += (dy / dist) * push
                }
            }
        }

        if (introEase < 1) {
            finalX = introStartX[i] + (finalX - introStartX[i]) * introEase
            finalY = introStartY[i] + (finalY - introStartY[i]) * introEase
        }

        pos[i * 3] = finalX
        pos[i * 3 + 1] = finalY
        pos[i * 3 + 2] = pZ[i]
    }

    geometry.attributes.position.needsUpdate = true
    renderer.render(scene, camera)
    requestAnimationFrame(tick)
}

tick()
